/* eslint-disable */
import React, { useState, useEffect } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Grid,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { formatTime } from "../common/util/formatter";
import ReportFilter from "./components/ReportFilter";
import { useTranslation } from "../common/components/LocalizationProvider";
import PageLayout from "../common/components/PageLayout";
import ReportsMenu from "./components/ReportsMenu";
import usePersistedState from "../common/util/usePersistedState";
import ColumnSelect from "./components/ColumnSelect";
import { useCatch } from "../reactHelper";
import useReportStyles from "./common/useReportStyles";
import TableShimmer from "../common/components/TableShimmer";
import LogoImage from "../login/LogoImage";
import { nativePostMessage } from "../common/components/NativeInterface";
import { sessionActions } from "../store";

// Colunas disponíveis com suas traduções
const columnsArray = [
  ["eventTime", "costDate"],
  ["price", "sharedPrice"],
  ["type", "costCategory"],
];

// Cria o Map
const columnsMap = new Map(columnsArray);

// Componente para cabeçalho de impressão
const PrintHeader = ({ deviceName, from, to }) => {
  const t = useTranslation();

  return (
    <div style={{ padding: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <LogoImage
          style={{ height: 60 }}
          onLoad={async () => {
            requestAnimationFrame(() =>
              requestAnimationFrame(() => {
                window.print();
                nativePostMessage(`print|${t('reportCostsResume')}.pdf`);
              })
            );
          }}
          alt={t('companyLogo')}
        />
        <h1 style={{ fontSize: 24, margin: 0 }}>{t('reportCostsResume')}</h1>
      </div>
      <div style={{ marginTop: 10, fontSize: 14, color: '#555' }}>
        {deviceName && (
          <div>
            <strong>{t('sharedDevice')}:</strong> {deviceName}
          </div>
        )}
        {from && (
          <div>
            <strong>{t('reportFrom')}:</strong> {new Date(from).toLocaleString()}
          </div>
        )}
        {to && (
          <div>
            <strong>{t('reportTo')}:</strong> {new Date(to).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
};

const CostsResumeReportPage = () => {
  const classes = useReportStyles();
  const t = useTranslation();
  const dispatch = useDispatch();

  // Sem tipagem no useSelector
  const devices = useSelector((state) => state.devices.items);
  const printing = useSelector((state) => state.session.printing);

  const [columns, setColumns] = usePersistedState("costsResumeColumns", [
    "eventTime",
    "type",
    "price",
  ]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Adicionar evento de afterprint para limpar o estado de impressão
  useEffect(() => {
    const handleAfterPrint = () => {
      dispatch(sessionActions.setPrinting(false));
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [dispatch]);

  const handleSubmit = useCatch(
    async ({ deviceId, from, to, type, mail, headers }) => {
      // Salvar parâmetros para impressão
      setSelectedDeviceId(deviceId);
      setFromDate(from);
      setToDate(to);

      if (type === 'pdf') {
        // Ativar modo de impressão após buscar os dados
        fetchData({ deviceId, from, to, mail, headers }).then(() => {
          dispatch(sessionActions.setPrinting(true));
        });
        return;
      }

      await fetchData({ deviceId, from, to, mail, headers });
    }
  );

  // Função para buscar dados
  const fetchData = async ({ deviceId, from, to, mail, headers }) => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ from, to, mail });
      query.append("deviceId", deviceId);
      const response = await fetch(
        `/api/reports/costs_resume?${query.toString()}`,
        {
          headers,
        }
      );

      if (response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType) {
          if (contentType === "application/json") {
            const data = await response.json();
            setItems(data);
            return data;
          } else {
            window.location.assign(
              window.URL.createObjectURL(await response.blob())
            );
          }
        }
      } else {
        throw Error(await response.text());
      }
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const formatValue = (item, key) => {
    switch (key) {
      case "deviceId":
        return devices[item[key]]?.name || item[key];
      case "eventTime":
        return item[key] ? formatTime(item[key], "DD/MM/YYYY") : null;
      default:
        return item[key];
    }
  };

  // Layout para modo de impressão
  if (printing) {
    const deviceName = selectedDeviceId ? devices[selectedDeviceId]?.name : '';

    return (
      <div>
        <PrintHeader
          deviceName={deviceName}
          from={fromDate}
          to={toDate}
        />
        <div>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((key) => (
                  <TableCell key={key}>
                    {t(columnsMap.get(key) || key)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={`item_${index}`}>
                  {columns.map((key) => (
                    <TableCell key={key}>{formatValue(item, key)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  // O conteúdo que será passado como children para o PageLayout
  const pageContent = (
    <>
      <div className={classes.header}>
        <ReportFilter
          handleSubmit={handleSubmit}
          handleSchedule={undefined}
          showOnly={undefined}
          ignoreDevice={undefined}
          multiDevice={undefined}
          includeGroups={undefined}
          loading={loading}
        >
          <Grid item xs={12} sm={12}>
            <ColumnSelect
              columns={columns}
              setColumns={setColumns}
              columnsArray={columnsArray}
              rawValues={true}
              disabled={loading}
            />
          </Grid>
        </ReportFilter>
      </div>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((key) => (
              <TableCell key={key}>
                {t(columnsMap.get(key) || key)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading ? (
            items.map((item, index) => (
              <TableRow key={`item_${index}_${new Date().getMilliseconds()}`}>
                {columns.map((key) => (
                  <TableCell key={key}>{formatValue(item, key)}</TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableShimmer 
              columns={columns.length} 
              startAction={false} 
              endAction={false}
            />
          )}
        </TableBody>
      </Table>
    </>
  );

  return (
    <PageLayout
      menu={<ReportsMenu />}
      breadcrumbs={["reportTitle", "reportCostsResume"]}
    >
      {pageContent}
    </PageLayout>
  );
};

export default CostsResumeReportPage;